import {observer} from 'mobx-react';
import '../geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import {Props as NOProps} from '../no-filter';
import FilterContainer from '../filter-container';

type Props = {
    showRosterSelection?: boolean;
};

const DisruptionAnalysisFilter = observer((props: Props) => {
    const {showRosterSelection = false} = props;
    const noStore = useContext(NetworkContext);

    useEffect(() => {
        if (noStore.runId &&
            noStore.availableDisruptionAnalysisFilters.length === 0 &&
            !noStore.loadingDisruptionAnalysisData) {
            noStore.loadDisruptiveAnalysisFilterData();
        }
    }, [noStore.runId]);

    const lineOfBusinessFilter: NOProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey('DISRUPTION_ANALYSIS', 'standard_type');
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('standard_type', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'Line of Business',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.standard_type,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            ['data-testid']: 'line-of-business-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    const patientRosterFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedDisruptionAnalysisFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'patient_roster_name',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('patient_roster_name', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'Patient Roster',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
    ]);

    const stateFilter: NOProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'state_additional_info.state',
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('state', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'State',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.state,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            ['data-testid']: 'state-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    const countyFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedDisruptionAnalysisFilter.standard_type,
            'state_additional_info.state': noStore.appliedDisruptionAnalysisFilter.state,
            patient_roster_name: noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            ['county_additional_info.county_name', 'ssacd'],
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('county', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        const disabled =
            !noStore.appliedDisruptionAnalysisFilter.state ||
            !noStore.appliedDisruptionAnalysisFilter.patient_roster_name;
        return {
            label: 'County',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.county,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            disabled,
            ['data-testid']: 'county-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.county,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    const zipFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            ssacd: noStore.disruptionAnalysisFilters.county,
            'state_additional_info.state': noStore.appliedDisruptionAnalysisFilter.state,
            patient_roster_name: noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
            standard_type: noStore.appliedDisruptionAnalysisFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'zip',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('zip', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        const disabled =
            !noStore.appliedDisruptionAnalysisFilter.state ||
            !noStore.appliedDisruptionAnalysisFilter.patient_roster_name ||
            !noStore.appliedDisruptionAnalysisFilter.county;
        return {
            label: 'ZIP',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.zip,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            disabled,
            ['data-testid']: 'zip-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.county,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
        noStore.appliedDisruptionAnalysisFilter.zip,
    ]);

    const specialtyFilter: NOProps = useMemo(() => {
        const source =
            noStore.defaultComplianceScores.length > 0
                ? noStore.defaultComplianceScores
                : noStore.complianceScores;
        const data = source.map((score) => ({
            name: score.name,
            compliance: noStore.defaultComplianceScores.length > 0 ? '' : score.compliance,
        }));
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisSpecialtyFilter(e);
        };
        const children: [(item: any) => string, (item: any) => ReactNode] = [
            (item: any): string => item.name,
            (item: any): ReactNode => item.name || 'All',
        ];
        const disabled = !noStore.appliedDisruptionAnalysisFilter.standard_type;
        return {
            label: 'Specialty',
            data,
            value: noStore.disruptionAnalysisSpecialtyFilter,
            onChange,
            children,
            spinner: noStore.loadingSpecialtyData,
            disabled,
            itemToString: (item: any) => item.name,
            ['data-testid']: 'specialty-dropdown',
        };
    }, [
        noStore.runId,
        noStore.defaultComplianceScores,
        noStore.complianceScores,
        noStore.disruptionAnalysisSpecialtyFilter,
        noStore.loadingSpecialtyData,
        noStore.appliedDisruptionAnalysisFilter.county,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    useEffect(() => {
        if (!noStore.appliedDisruptionAnalysisFilter.standard_type) {
            noStore.setDisruptionAnalysisSpecialtyFilter('');
        }
    }, [noStore.appliedDisruptionAnalysisFilter.standard_type]);

    const filterConfiguration: Array<NOProps> = [
        lineOfBusinessFilter,
        patientRosterFilter,
        stateFilter,
        countyFilter,
        zipFilter,
        specialtyFilter,
    ];

    return (
        <FilterContainer
            showRosterSelection={showRosterSelection}
            filterConfiguration={filterConfiguration}
        />
    );
});

export default DisruptionAnalysisFilter;
